import { HouseholdModelPortfolioAllocation } from '@ovComponents/4-module/widgets/householdModelPortfolio/components/householdModelPortfolioAllocation';
import { HouseholdModelPortfolioIps } from '@ovComponents/4-module/widgets/householdModelPortfolio/components/householdModelPortfolioIps';
import { HouseholdModelPortfolioModelDetails } from '@ovComponents/4-module/widgets/householdModelPortfolio/components/householdModelPortfolioModelDetails';
import {
  HOUSEHOLD_MODEL_PORTFOLIO_TABS,
  IPS_FIELD_OPTIONS,
  MODEL_DETAIL_FIELD_OPTIONS,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.constants';
import type {
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioTab,
  HouseholdModelPortfolioWidgetOptions,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { Box, Tab, TabPanel, Tabs } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InitializeHouseholdModelPortfolioOptions } from './initializeHouseholdModelPortfolio.visual';

type DraftPreviewShowOptionKey =
  | (typeof MODEL_DETAIL_FIELD_OPTIONS)[number]
  | (typeof IPS_FIELD_OPTIONS)[number];

/**
 * Every show* toggle on the Initialize step defaults to visible — mirrors
 * workflow-service's `default: true` on each of these options. An org only
 * sees a field hidden here if it explicitly turned that field off for this
 * step, so an unconfigured (or not-yet-loaded) step keeps showing everything,
 * same as before this preview read real options.
 *
 * Taken from the widget's own option lists rather than spelled out, so a toggle
 * added there cannot end up hidden here by being forgotten. Most of the
 * client-facing defaults are `false`, and a field left out of this step is
 * silent: the advisor commits a model without having been shown it.
 */
const DRAFT_PREVIEW_SHOW_OPTION_DEFAULTS = Object.fromEntries(
  [...MODEL_DETAIL_FIELD_OPTIONS, ...IPS_FIELD_OPTIONS].map((optionKey) => [
    optionKey,
    true,
  ]),
) as Record<DraftPreviewShowOptionKey, boolean>;

/**
 * Applies the Initialize step's own "absent means on" defaults before handing
 * options to the widget's read-only renderers, which otherwise fall back to
 * the client-facing widget's defaults (several of which default to hidden —
 * wrong for this admin review step).
 *
 * Filled in key by key rather than spread over: a stored option can hold an
 * explicit null, which a spread hands on as the answer, and the renderers read
 * that as nothing configured and fall back to the client-facing default.
 */
const buildDraftPreviewOptions = (
  options: InitializeHouseholdModelPortfolioOptions,
): HouseholdModelPortfolioWidgetOptions => {
  const previewOptions: HouseholdModelPortfolioWidgetOptions = { ...options };

  for (const optionKey of Object.keys(
    DRAFT_PREVIEW_SHOW_OPTION_DEFAULTS,
  ) as DraftPreviewShowOptionKey[]) {
    previewOptions[optionKey] ??= DRAFT_PREVIEW_SHOW_OPTION_DEFAULTS[optionKey];
  }

  return previewOptions;
};

export type HouseholdModelPortfolioDraftPreviewProps = {
  model: HouseholdModelPortfolioModel;
  options: InitializeHouseholdModelPortfolioOptions;
};

/**
 * Read-only Model/IPS preview of the draft model portfolio being built up over
 * the course of the Initialize workflow. Reuses the household widget's own
 * read-only section renderers (`action='edit'` just means "there's a model to
 * show" to those components) rather than re-implementing their field lists,
 * skeletons, and empty states. Every section (Details, Allocations, IPS) is
 * edited in its own builder-configured step, so this final step is purely a
 * review before commit.
 */
export const HouseholdModelPortfolioDraftPreview = ({
  model,
  options,
}: HouseholdModelPortfolioDraftPreviewProps) => {
  const { t } = useTranslation(['components']);
  const [activeTab, setActiveTab] = useState<HouseholdModelPortfolioTab>(
    HOUSEHOLD_MODEL_PORTFOLIO_TABS.MODEL,
  );
  const previewOptions = buildDraftPreviewOptions(options);

  return (
    <Box
      data-testid='household-initialize-draft-preview'
      sx={{
        mb: 3,
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_event, newValue: HouseholdModelPortfolioTab) => {
          setActiveTab(newValue);
        }}
      >
        <Tab
          data-testid='household-initialize-draft-preview-tab-model'
          label={t('components:householdModelPortfolio.tabs.model')}
          value={HOUSEHOLD_MODEL_PORTFOLIO_TABS.MODEL}
        />
        <Tab
          data-testid='household-initialize-draft-preview-tab-ips'
          label={t('components:householdModelPortfolio.tabs.ips')}
          value={HOUSEHOLD_MODEL_PORTFOLIO_TABS.IPS}
        />
      </Tabs>

      <TabPanel value={activeTab} index={HOUSEHOLD_MODEL_PORTFOLIO_TABS.MODEL}>
        <Box data-testid='household-initialize-draft-preview-model-tab-panel'>
          <HouseholdModelPortfolioModelDetails
            action='edit'
            options={previewOptions}
            model={model}
            modelDetailsLoading={false}
          />
          <HouseholdModelPortfolioAllocation
            action='edit'
            model={model}
            modelDetailsLoading={false}
          />
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={HOUSEHOLD_MODEL_PORTFOLIO_TABS.IPS}>
        <Box data-testid='household-initialize-draft-preview-ips-tab-panel'>
          <HouseholdModelPortfolioIps
            action='edit'
            options={previewOptions}
            model={model}
            modelDetailsLoading={false}
          />
        </Box>
      </TabPanel>
    </Box>
  );
};
